// AI Agents Data
export const AI_AGENTS = [
  {
    id: 'dev-agent',
    name: 'Dev Agent',
    shortName: 'DEV',
    tagline: 'AI-Powered Code Generation',
    description: 'Our Dev Agent writes production-ready code across multiple languages and frameworks, accelerating development by 60%.',
    color: '#00F0FF',
    capabilities: [
      'Full-stack code generation',
      'API development & integration',
      'Database schema design',
      'Unit test creation',
      'Code refactoring & optimization'
    ],
    techStack: ['React', 'Python', 'Node.js', 'Java', 'Go'],
    metrics: { speed: 95, accuracy: 92, coverage: 88 }
  },
  {
    id: 'test-agent',
    name: 'Test Agent',
    shortName: 'TEST',
    tagline: 'Automated Quality Assurance',
    description: 'Comprehensive automated testing with 99% coverage. Finds bugs before they reach production.',
    color: '#00FF94',
    capabilities: [
      'Unit & integration testing',
      'E2E test automation',
      'Performance testing',
      'Security vulnerability scanning',
      'Regression test suites'
    ],
    techStack: ['Playwright', 'Jest', 'Selenium', 'K6', 'OWASP'],
    metrics: { speed: 90, accuracy: 98, coverage: 95 }
  },
  {
    id: 'review-agent',
    name: 'Review Agent',
    shortName: 'CODE',
    tagline: 'Intelligent Code Review',
    description: 'AI-powered code review that catches issues, suggests improvements, and enforces best practices.',
    color: '#7000FF',
    capabilities: [
      'Automated code review',
      'Best practice enforcement',
      'Security pattern detection',
      'Performance optimization tips',
      'Documentation suggestions'
    ],
    techStack: ['SonarQube', 'ESLint', 'Prettier', 'CodeClimate'],
    metrics: { speed: 85, accuracy: 94, coverage: 90 }
  },
  {
    id: 'ui-agent',
    name: 'UI Agent',
    shortName: 'UI',
    tagline: 'Design-to-Code Automation',
    description: 'Transforms designs into pixel-perfect, responsive UI components with accessibility built-in.',
    color: '#FFD600',
    capabilities: [
      'Figma to code conversion',
      'Responsive design implementation',
      'Component library generation',
      'Accessibility compliance (WCAG)',
      'Design system creation'
    ],
    techStack: ['React', 'Tailwind', 'Storybook', 'Figma API'],
    metrics: { speed: 88, accuracy: 91, coverage: 85 }
  },
  {
    id: 'devops-agent',
    name: 'DevOps Agent',
    shortName: 'OPS',
    tagline: 'Infrastructure Automation',
    description: 'Automates CI/CD pipelines, cloud infrastructure, and deployment workflows.',
    color: '#FF6B00',
    capabilities: [
      'CI/CD pipeline setup',
      'Infrastructure as Code',
      'Container orchestration',
      'Cloud migration',
      'Monitoring & alerting setup'
    ],
    techStack: ['AWS', 'Docker', 'Kubernetes', 'Terraform', 'GitHub Actions'],
    metrics: { speed: 92, accuracy: 96, coverage: 89 }
  },
  {
    id: 'docs-agent',
    name: 'Docs Agent',
    shortName: 'DOCS',
    tagline: 'Intelligent Documentation',
    description: 'Generates comprehensive technical documentation, API docs, and user guides automatically.',
    color: '#00B4D8',
    capabilities: [
      'API documentation generation',
      'Technical spec writing',
      'User guide creation',
      'Code comment generation',
      'README automation'
    ],
    techStack: ['OpenAPI', 'Swagger', 'Markdown', 'Docusaurus'],
    metrics: { speed: 94, accuracy: 89, coverage: 92 }
  },
  {
    id: 'support-agent',
    name: 'Support Agent',
    shortName: 'HELP',
    tagline: '24/7 Intelligent Support',
    description: 'Provides instant technical support, troubleshooting, and issue resolution.',
    color: '#E91E63',
    capabilities: [
      'Ticket triage & routing',
      'Knowledge base search',
      'Automated troubleshooting',
      'Escalation management',
      'SLA monitoring'
    ],
    techStack: ['NLP', 'Zendesk', 'Intercom', 'Slack'],
    metrics: { speed: 97, accuracy: 87, coverage: 91 }
  },
  {
    id: 'data-agent',
    name: 'Data Agent',
    shortName: 'DATA',
    tagline: 'Analytics & Insights',
    description: 'Processes and analyzes data to generate actionable business insights.',
    color: '#9C27B0',
    capabilities: [
      'Data pipeline creation',
      'ETL automation',
      'Dashboard generation',
      'Predictive analytics',
      'Report automation'
    ],
    techStack: ['Python', 'SQL', 'Pandas', 'Tableau', 'Power BI'],
    metrics: { speed: 86, accuracy: 93, coverage: 88 }
  }
];

// Services Data - Agentic Workflow Services
export const SERVICES = [
  {
    id: 'erp-crm-agents',
    name: 'ERP & CRM Agents',
    shortName: 'ERP/CRM',
    description: 'AI agents that automate your enterprise workflows. Integrate directly into SAP, Salesforce, HubSpot, NetSuite, Zoho, and Microsoft Dynamics.',
    icon: 'Buildings',
    features: ['SAP / NetSuite / Dynamics agents', 'Salesforce / HubSpot / Zoho automation', 'Quote-to-cash workflows', 'Invoice OCR & reconciliation', 'Sales forecasting agents', 'Lead scoring & routing'],
    industries: ['Manufacturing', 'Logistics', 'Finance', 'Healthcare', 'Retail'],
    headline: 'Stop clicking. Start shipping.',
    sub: 'We build AI agents that live inside your ERP/CRM and do the repetitive work — quote generation, invoice matching, lead routing, forecasting — so your team works on strategy, not data entry.',
    outcomes: ['70% less manual data entry', '4x faster quote-to-cash', 'Zero invoice reconciliation backlog'],
  },
  {
    id: 'whatsapp-bots',
    name: 'WhatsApp Bot Flows',
    shortName: 'WhatsApp',
    description: 'Move any business process to WhatsApp. Customer support, order tracking, lead nurture, appointments, payments — all automated.',
    icon: 'ChatCircle',
    features: ['Order taking & menu bots', 'Customer support agents', 'Appointment booking flows', 'Payment collection via UPI/Stripe', 'WhatsApp catalog commerce', 'Twilio / Qikchat / Meta API'],
    industries: ['Restaurants', 'E-commerce', 'Healthcare', 'Real Estate', 'Education'],
    headline: 'Your customers already live on WhatsApp.',
    sub: 'We build conversational agents that handle orders, support, bookings, payments, and reminders — turning WhatsApp into your busiest sales channel.',
    outcomes: ['3x higher engagement vs email', '24/7 support without staff', '40% lower CAC'],
  },
  {
    id: 'dashboard-analytics',
    name: 'Dashboard & Analytics Agents',
    shortName: 'Dashboards',
    description: 'Intelligent dashboards that surface insights automatically. Connect any data source, get answers in plain English.',
    icon: 'ChartLine',
    features: ['Real-time KPI dashboards', 'AI-narrated insights', 'Ask-your-data chat (NLQ)', 'Anomaly detection', 'Auto-generated reports', 'Snowflake / BigQuery / Postgres'],
    industries: ['SaaS', 'Finance', 'E-commerce', 'Marketing', 'Operations'],
    headline: 'Dashboards that explain themselves.',
    sub: 'Stop staring at charts. Our analytics agents narrate what changed, why, and what to do next — in plain English, every morning, in your inbox or Slack.',
    outcomes: ['10-minute morning briefing', '5x faster anomaly detection', 'Self-serve answers for non-analysts'],
  },
  {
    id: 'mobile-agents',
    name: 'Mobile App Agents',
    shortName: 'Mobile',
    description: 'Native and cross-platform mobile experiences with embedded AI agents — voice, vision, recommendations, automation.',
    icon: 'DeviceMobile',
    features: ['React Native / Flutter', 'iOS & Android native', 'Voice & vision agents', 'Smart push notifications', 'Offline-first sync', 'App Store optimization'],
    industries: ['Fintech', 'Healthcare', 'Retail', 'Logistics', 'Consumer'],
    headline: 'Mobile apps that think for the user.',
    sub: 'We embed AI agents inside iOS and Android apps so they anticipate user intent, automate workflows, and feel like a personal assistant — not just another screen.',
    outcomes: ['60% faster ship-to-store', 'AI agents reduce taps by 40%', 'Native performance, hybrid speed'],
  },
  {
    id: 'product-development',
    name: 'New Product Development',
    shortName: 'Product',
    description: 'From idea to MVP in weeks. Our AI agents accelerate every phase — research, design, build, test, deploy.',
    icon: 'Lightning',
    features: ['Idea → MVP in 4-6 weeks', 'AI-paired product design', 'Full-stack engineering', 'CI/CD & DevOps', 'PMF analytics', 'Co-build with your team'],
    industries: ['Startups', 'Enterprises', 'Venture Studios'],
    headline: 'Idea on Monday. MVP shipped by month-end.',
    sub: 'Our hybrid AI + senior engineering team takes your concept and ships a real, scalable MVP in weeks — not quarters. 500+ products shipped, 98% client satisfaction.',
    outcomes: ['4-6 week MVPs', '60% faster than traditional agencies', 'Production-grade, not throwaway'],
  },
  {
    id: 'website-development',
    name: 'Website Development',
    shortName: 'Web',
    description: 'Modern websites and web apps built for speed, conversion, and SEO. Headless CMS, Next.js, Tailwind, Vercel.',
    icon: 'Globe',
    features: ['Next.js / React / Vue', 'Headless CMS (Sanity, Strapi)', 'A/B testing & CRO', 'Core Web Vitals tuning', 'SEO-first architecture', 'Vercel / Cloudflare edge'],
    industries: ['SaaS', 'E-commerce', 'Media', 'Agencies', 'Enterprise'],
    headline: 'Websites that load instantly and convert harder.',
    sub: 'Fast. Beautiful. SEO-ready. We build websites and marketing sites that Google loves and users actually buy from.',
    outcomes: ['100/100 Lighthouse scores', '2.5x conversion lift on average', 'Top-3 SERP rankings'],
  },
  {
    id: 'ai-seo',
    name: 'AI SEO Agents',
    shortName: 'AI SEO',
    description: 'AI-powered SEO automation. Content generation, on-page optimization, technical audits, backlink intelligence — all hands-free.',
    icon: 'MagnifyingGlass',
    features: ['AI content generation at scale', 'Programmatic SEO pages', 'Technical SEO audits', 'Schema markup automation', 'Backlink intelligence', 'Rank tracking & alerts'],
    industries: ['SaaS', 'E-commerce', 'Marketplaces', 'Local Businesses'],
    headline: 'Rank higher without lifting a finger.',
    sub: 'Our SEO agents write content, fix technical issues, build internal links, and publish programmatic pages — all autonomously. You watch the rankings climb.',
    outcomes: ['1000+ SEO pages auto-published', '3-6 month organic traffic 3x', 'Hands-free content engine'],
  }
];

// Industries Data
export const INDUSTRIES = [
  {
    id: 'fintech',
    name: 'Fintech & Banking',
    slug: 'fintech',
    description: 'Digital banking, payment systems, and financial technology solutions.',
    icon: 'Bank',
    solutions: ['Digital Banking Apps', 'Payment Gateways', 'Trading Platforms', 'Fraud Detection', 'Regulatory Compliance'],
    stats: { projects: 50, clients: 25 }
  },
  {
    id: 'healthcare',
    name: 'Healthcare & Life Sciences',
    slug: 'healthcare',
    description: 'Healthcare technology, telemedicine, and medical data solutions.',
    icon: 'Heartbeat',
    solutions: ['Telemedicine Platforms', 'EHR Systems', 'Medical Imaging AI', 'Patient Portals', 'HIPAA Compliance'],
    stats: { projects: 35, clients: 18 }
  },
  {
    id: 'ecommerce',
    name: 'E-commerce & Retail',
    slug: 'ecommerce',
    description: 'Online stores, marketplaces, and omnichannel retail solutions.',
    icon: 'ShoppingCart',
    solutions: ['Custom Storefronts', 'Marketplace Platforms', 'Inventory Systems', 'Recommendation Engines', 'POS Integration'],
    stats: { projects: 60, clients: 30 }
  },
  {
    id: 'saas',
    name: 'SaaS & Software',
    slug: 'saas',
    description: 'Software-as-a-Service products and B2B software solutions.',
    icon: 'Desktop',
    solutions: ['Multi-tenant Architecture', 'Subscription Billing', 'API Platforms', 'Analytics Dashboards', 'White-labeling'],
    stats: { projects: 80, clients: 45 }
  },
  {
    id: 'edtech',
    name: 'Education Technology',
    slug: 'edtech',
    description: 'Learning management systems and educational platforms.',
    icon: 'GraduationCap',
    solutions: ['LMS Platforms', 'Virtual Classrooms', 'Assessment Tools', 'Course Marketplaces', 'Student Analytics'],
    stats: { projects: 25, clients: 15 }
  },
  {
    id: 'logistics',
    name: 'Logistics & Supply Chain',
    slug: 'logistics',
    description: 'Supply chain management and logistics optimization solutions.',
    icon: 'Truck',
    solutions: ['Fleet Management', 'Route Optimization', 'Warehouse Systems', 'Tracking Platforms', 'Demand Forecasting'],
    stats: { projects: 30, clients: 20 }
  },
  {
    id: 'proptech',
    name: 'Real Estate & PropTech',
    slug: 'proptech',
    description: 'Property technology and real estate management solutions.',
    icon: 'House',
    solutions: ['Property Portals', 'Tenant Management', 'Virtual Tours', 'Smart Building IoT', 'Transaction Platforms'],
    stats: { projects: 20, clients: 12 }
  },
  {
    id: 'insurance',
    name: 'Insurance',
    slug: 'insurance',
    description: 'Insurance technology and claims management systems.',
    icon: 'Shield',
    solutions: ['Claims Processing', 'Policy Management', 'Risk Assessment AI', 'Customer Portals', 'Underwriting Automation'],
    stats: { projects: 28, clients: 16 }
  }
];

// Technology Stack Data
export const TECH_STACK = {
  frontend: [
    { id: 'react', name: 'React', icon: 'Atom', expertise: 'Expert', years: 8 },
    { id: 'nextjs', name: 'Next.js', icon: 'Lightning', expertise: 'Expert', years: 6 },
    { id: 'vue', name: 'Vue.js', icon: 'Atom', expertise: 'Advanced', years: 5 },
    { id: 'angular', name: 'Angular', icon: 'Atom', expertise: 'Advanced', years: 7 },
    { id: 'typescript', name: 'TypeScript', icon: 'Code', expertise: 'Expert', years: 6 }
  ],
  backend: [
    { id: 'python', name: 'Python', icon: 'Code', expertise: 'Expert', years: 10 },
    { id: 'nodejs', name: 'Node.js', icon: 'Lightning', expertise: 'Expert', years: 9 },
    { id: 'java', name: 'Java', icon: 'Coffee', expertise: 'Expert', years: 12 },
    { id: 'golang', name: 'Go', icon: 'Lightning', expertise: 'Advanced', years: 5 },
    { id: 'dotnet', name: '.NET', icon: 'Code', expertise: 'Advanced', years: 8 }
  ],
  mobile: [
    { id: 'react-native', name: 'React Native', icon: 'DeviceMobile', expertise: 'Expert', years: 6 },
    { id: 'flutter', name: 'Flutter', icon: 'DeviceMobile', expertise: 'Expert', years: 5 },
    { id: 'swift', name: 'Swift', icon: 'Apple', expertise: 'Advanced', years: 8 },
    { id: 'kotlin', name: 'Kotlin', icon: 'Android', expertise: 'Advanced', years: 6 }
  ],
  database: [
    { id: 'postgresql', name: 'PostgreSQL', icon: 'Database', expertise: 'Expert', years: 10 },
    { id: 'mongodb', name: 'MongoDB', icon: 'Database', expertise: 'Expert', years: 8 },
    { id: 'mysql', name: 'MySQL', icon: 'Database', expertise: 'Expert', years: 12 },
    { id: 'redis', name: 'Redis', icon: 'Lightning', expertise: 'Advanced', years: 7 }
  ],
  cloud: [
    { id: 'aws', name: 'AWS', icon: 'Cloud', expertise: 'Expert', years: 10 },
    { id: 'gcp', name: 'Google Cloud', icon: 'Cloud', expertise: 'Expert', years: 7 },
    { id: 'azure', name: 'Azure', icon: 'Cloud', expertise: 'Advanced', years: 6 },
    { id: 'docker', name: 'Docker', icon: 'Package', expertise: 'Expert', years: 8 },
    { id: 'kubernetes', name: 'Kubernetes', icon: 'Network', expertise: 'Expert', years: 6 }
  ]
};

// Regions Data
export const REGIONS = [
  { code: 'US', name: 'United States', flag: '🇺🇸', timezone: 'EST/PST', currency: 'USD' },
  { code: 'UK', name: 'United Kingdom', flag: '🇬🇧', timezone: 'GMT', currency: 'GBP' },
  { code: 'EU', name: 'Europe', flag: '🇪🇺', timezone: 'CET', currency: 'EUR' },
  { code: 'AU', name: 'Australia', flag: '🇦🇺', timezone: 'AEST', currency: 'AUD' }
];

// Pricing Models
export const PRICING_MODELS = [
  {
    id: 'rapid-sprint',
    name: 'Rapid Sprint',
    description: 'Fixed-price MVP builds in 7-14 days',
    startingPrice: 5000,
    currency: 'USD',
    duration: '1-2 weeks',
    features: [
      'AI-accelerated development',
      'Full-stack MVP',
      'Cloud deployment',
      'Source code ownership',
      '2 revision rounds',
      'Technical documentation'
    ],
    bestFor: 'Startups validating ideas',
    popular: false
  },
  {
    id: 'dedicated-team',
    name: 'Dedicated Hybrid Team',
    description: 'AI + human engineering team on retainer',
    startingPrice: 3500,
    priceUnit: 'per developer/month',
    currency: 'USD',
    duration: 'Monthly',
    features: [
      'Dedicated AI + human team',
      'Daily standups',
      'Sprint-based delivery',
      'Flexible scaling',
      'Full transparency',
      'Git access & documentation'
    ],
    bestFor: 'Growing products',
    popular: true
  },
  {
    id: 'enterprise',
    name: 'Enterprise Engagement',
    description: 'Custom solutions with SLA',
    startingPrice: null,
    priceLabel: 'Custom Quote',
    currency: 'USD',
    duration: 'Long-term',
    features: [
      'Dedicated account manager',
      'Custom SLA',
      'Priority support 24/7',
      'Security compliance',
      'On-premise options',
      'Training & handoff'
    ],
    bestFor: 'Enterprise scale',
    popular: false
  }
];

// Company Stats
export const COMPANY_STATS = [
  { label: 'Projects Delivered', value: '500+', icon: 'FolderOpen' },
  { label: 'Client Satisfaction', value: '98%', icon: 'Heart' },
  { label: 'AI Acceleration', value: '60%', icon: 'Lightning' },
  { label: 'Global Clients', value: '150+', icon: 'Globe' },
  { label: 'Team Members', value: '50+', icon: 'Users' },
  { label: 'Years Experience', value: '20+', icon: 'Calendar' }
];

// Trust Badges
export const TRUST_BADGES = [
  'GDPR Compliant',
  'SOC 2 Ready',
  'NDA Ready',
  'Full Code Ownership',
  'US Timezone Availability',
  '24/7 Support'
];
